import React, { useEffect, useRef } from 'react';
import { APP_CONFIG } from '../../config/app.config';
import canadaFlag from '../../Assets/locations/canada-flag.png';
import indiaFlag  from '../../Assets/locations/india-flag.png';
import usaFlag    from '../../Assets/locations/usa-flag.png';

/* ── location data ───────────────────────────────────── */
const locations = [
    {
        id: 1,
        country: 'Canada HQ',
        city: 'Mississauga, ON',
        flag: canadaFlag,
        address: 'Corporate House\nUnit 201, 2970 Drew Road,\nMississauga, ON L4T 0A6',
        phone: '+1 (647) 855-6177',
        email: 'info@orientalbiz.ca',
        mapLink: APP_CONFIG.addressLink,
        primary: true,          // highlighted card
    },
    {
        id: 2,
        country: 'Brampton, Canada',
        city: 'Brampton, ON',
        flag: canadaFlag,
        address: '80 Culture Cres,\nBrampton, ON, L6X 5A2',
        phone: null,
        email: 'info@orientalbiz.ca',
        mapLink: 'https://maps.google.com/?q=80+Culture+Cres,Brampton,ON,L6X+5A2',
        primary: false,
    },
    {
        id: 3,
        country: 'USA',
        city: 'Fremont, CA',
        flag: usaFlag,
        address: '1500 WHITE BIRCH TER\nFREMONT, CA – 94536',
        phone: null,
        email: 'info@orientalbiz.ca',
        mapLink: 'https://maps.google.com/?q=1500+White+Birch+Ter,Fremont,CA,94536',
        primary: false,
    },
    {
        id: 4,
        country: 'India',
        city: 'Chennai, TN',
        flag: indiaFlag,
        address: 'Oriental Business Solutions Pvt Ltd\n154 Shivani Palace, Revathipuram 4th St,\nUrappakkam, Chennai 603210',
        phone: null,
        email: 'info@orientalbiz.ca',
        mapLink: 'https://maps.google.com/?q=Urappakkam,Chennai,India',
        primary: false,
    },
];

/* ── small inline SVG icons ─────────────────────────── */
const PinIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
        <circle cx="12" cy="10" r="3"/>
    </svg>
);
const PhoneIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18 2 2 0 012.22 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.56-.56a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
    </svg>
);
const MailIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
    </svg>
);
const ArrowIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);

/* ════════════════════════════════════════════════════════ */
const OurLocations = () => {
    const cardsRef = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('loc-card--visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12 }
        );
        cardsRef.current.forEach((el) => { if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    return (
        <section className="loc-section">

            {/* ── header ── */}
            <div className="loc-header">
                <span className="loc-header__label">GLOBAL PRESENCE</span>
                <h2 className="loc-header__title">Our Locations</h2>
                <p className="loc-header__sub">
                    Serving clients across Canada, the USA, and India with trusted tax and business expertise.
                </p>
                <div className="loc-header__line" />
            </div>

            {/* ── cards ── */}
            <div className="loc-grid container-xxl">
                {locations.map((loc, idx) => (
                    <div
                        key={loc.id}
                        className={`loc-card${loc.primary ? ' loc-card--primary' : ''}`}
                        ref={(el) => (cardsRef.current[idx] = el)}
                        style={{ '--delay': `${idx * 120}ms` }}
                    >
                        {/* flag + country header */}
                        <div className="loc-card__top">
                            <img src={loc.flag} alt={loc.country} className="loc-card__flag" />
                            <div>
                                <h3 className="loc-card__country">{loc.country}</h3>
                                <p className="loc-card__city">{loc.city}</p>
                            </div>
                            {loc.primary && (
                                <span className="loc-card__badge">HQ</span>
                            )}
                        </div>

                        <div className="loc-card__divider" />

                        {/* details */}
                        <ul className="loc-card__details">
                            <li>
                                <PinIcon />
                                <span>{loc.address}</span>
                            </li>
                            {loc.phone && (
                                <li>
                                    <PhoneIcon />
                                    <a href={`tel:${loc.phone.replace(/\s/g, '')}`}>{loc.phone}</a>
                                </li>
                            )}
                            <li>
                                <MailIcon />
                                <a href={`mailto:${loc.email}`}>{loc.email}</a>
                            </li>
                        </ul>

                        {/* CTA */}
                        <a
                            href={loc.mapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="loc-card__btn"
                        >
                            View on Map <ArrowIcon />
                        </a>

                        {/* accent bar */}
                        <div className="loc-card__bar" />
                    </div>
                ))}
            </div>
        </section>
    );
};

export default OurLocations;
